// Wave 8 — AI draft button shared by every composer that supports AI.
// Renders inline next to the textarea/editor and exposes a tiny popover
// with kind-specific defaults. Disables itself when AI is off or out of
// budget. Surfaces a clear "out of credits" message rather than silently
// failing — both as a toast AND inline inside the popover, mirroring
// the Catch Me Up pattern so the rep doesn't have to retry just to read
// the message again.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { ToastAction } from "@/components/ui/toast";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { describeAiError, parseApiError, type FriendlyAiError } from "@/lib/ai-error";

type Kind = "marketing" | "drip" | "mass_email" | "standard_email" | "social_description";

export function AIDraftButton({
  kind,
  context,
  onDraft,
  buttonLabel = "Draft with AI",
  size = "sm",
  disabled,
}: {
  kind: Kind;
  context?: string;
  onDraft: (text: string) => void;
  buttonLabel?: string;
  size?: "sm" | "default";
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FriendlyAiError | null>(null);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { role } = useEffectivePermissions();
  const isAdmin = role === "admin";

  const balance = useQuery<any>({ queryKey: ["/api/ai/balance"], enabled: open });

  async function go() {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest("POST", "/api/ai/draft", { kind, prompt, context, maxOutputTokens: 600 });
      const data = await res.json();
      onDraft(data.text || "");
      setOpen(false);
      setPrompt("");
      toast({ title: "Drafted", description: `Used ~${data.costCents}¢ of credit.` });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      // Fall back to HTTP status when the upstream didn't include a
      // JSON `code` (e.g. proxy error pages) so the user still gets
      // the tailored "out of credits" / "AI assist is off" message.
      const code =
        parsed.code ??
        (parsed.status === 402
          ? "AI_BUDGET_EXCEEDED"
          : parsed.status === 403
            ? "AI_DISABLED"
            : undefined);
      const friendly = describeAiError(code, parsed.message, {
        isAdmin,
        defaultTitle: "AI draft failed",
        defaultMessage: "Couldn't generate a draft right now.",
      });
      setError(friendly);
      toast({
        title: friendly.title,
        description: friendly.message,
        variant: "destructive",
        action: friendly.action ? (
          <ToastAction
            altText={friendly.action.label}
            onClick={() => navigate(friendly.action!.href)}
          >
            {friendly.action.label}
          </ToastAction>
        ) : undefined,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleErrorAction(href: string) {
    setOpen(false);
    navigate(href);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setError(null);
  }

  const disabledByBudget = balance.data && !balance.data.enabled;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size}
          disabled={!!disabled}
          data-testid={`button-ai-draft-${kind}`}
        >
          <Sparkles className="h-4 w-4 mr-1.5" />
          {buttonLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-2">
          <div className="text-sm font-medium">Tell the AI what you need</div>
          <Textarea
            data-testid={`input-ai-prompt-${kind}`}
            placeholder={hintFor(kind)}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              if (error) setError(null);
            }}
            rows={4}
            disabled={loading}
          />
          {balance.data && (
            <div className="text-xs text-muted-foreground" data-testid={`text-ai-balance`}>
              {balance.data.enabled ? (
                <>
                  This month: {fmtCents(balance.data.monthUsageCents)} used / {fmtCents(balance.data.monthGiftedAllowanceCents)} gifted
                  {balance.data.paidRemainingCents > 0
                    ? ` · ${fmtCents(balance.data.paidRemainingCents)} paid credit`
                    : ""}
                </>
              ) : (
                "AI assist is currently disabled for this workspace."
              )}
            </div>
          )}
          {error && (
            <div
              className="space-y-2 py-2 border-t"
              data-testid={`ai-draft-error-${kind}`}
            >
              <div
                className="text-sm font-medium text-destructive"
                data-testid={`text-ai-draft-error-title-${kind}`}
              >
                {error.title}
              </div>
              <div
                className="text-sm text-muted-foreground"
                data-testid={`text-ai-draft-error-${kind}`}
              >
                {error.message}
              </div>
              {error.hint && (
                <div
                  className="text-xs text-muted-foreground"
                  data-testid={`text-ai-draft-error-hint-${kind}`}
                >
                  {error.hint}
                </div>
              )}
              {error.action && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleErrorAction(error.action!.href)}
                  data-testid={`button-ai-draft-error-action-${kind}`}
                >
                  {error.action.label}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={go}
              disabled={loading || !prompt.trim() || disabledByBudget}
              data-testid={`button-ai-go-${kind}`}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Generate
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function hintFor(kind: Kind): string {
  switch (kind) {
    case "marketing":
      return "e.g. 'Write a postcard for senior-living activity directors highlighting our holiday-special package.'";
    case "drip":
      return "e.g. 'Step 3 of nurture — gentle check-in two weeks after the last touch.'";
    case "mass_email":
      return "e.g. 'Announcing a new winter package available November–February.'";
    case "standard_email":
      return "e.g. 'Reply confirming the date and asking for the venue contact.'";
    case "social_description":
      return "e.g. 'Behind-the-scenes from last weekend's cabaret show in Dallas.'";
  }
}

function fmtCents(c: number) {
  if (c == null) return "$0.00";
  return `$${(c / 100).toFixed(2)}`;
}
